import React from 'react';
import { Button } from './ui/button';
import { CancelButton } from './ui/cancel-button';
import { DialogFooter, DialogPrimaryShortcut } from './ui/dialog';
import { RotatingDeleteIcon } from './RotatingDeleteIcon';
import { useLocale } from '../contexts/LocaleContext';

interface EditDialogFooterProps {
  isEditing: boolean;
  isSaving: boolean;
  saveDisabled?: boolean;
  onCancel: () => void;
  onSave: () => void;
  onDelete?: () => void;
  deleteDisabled?: boolean;
  deleteLabel?: string;
  onUnlink?: () => void;
  unlinkDisabled?: boolean;
  unlinkLabel?: string;
}

export const EditDialogFooter: React.FC<EditDialogFooterProps> = ({
  isEditing,
  isSaving,
  saveDisabled = false,
  onCancel,
  onSave,
  onDelete,
  deleteDisabled = false,
  deleteLabel,
  onUnlink,
  unlinkDisabled = false,
  unlinkLabel,
}) => {
  const { t } = useLocale();
  const showLeftActions = isEditing && (onDelete || onUnlink);

  return (
    <DialogFooter className="gap-2 sm:justify-between">
      {showLeftActions ? (
        <div className="flex flex-wrap gap-2">
          {onDelete ? (
            <Button
              type="button"
              variant="destructive"
              size="sm"
              disabled={deleteDisabled || isSaving}
              onClick={onDelete}
              className="group inline-flex items-center gap-1.5"
            >
              <RotatingDeleteIcon />
              {deleteLabel ?? t('delete')}
            </Button>
          ) : null}
          {onUnlink ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={unlinkDisabled || isSaving}
              onClick={onUnlink}
            >
              {unlinkLabel ?? t('unlink')}
            </Button>
          ) : null}
        </div>
      ) : (
        <span />
      )}
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <CancelButton onClick={onCancel} disabled={isSaving} />
        <Button
          type="button"
          size="sm"
          dialogPrimary
          className="inline-flex items-center gap-1.5"
          onClick={onSave}
          disabled={saveDisabled || isSaving}
        >
          {isSaving ? (
            isEditing ? t('saving') : t('adding')
          ) : (
            <>
              {isEditing ? t('save') : t('add')}
              <DialogPrimaryShortcut className="ml-0.5" />
            </>
          )}
        </Button>
      </div>
    </DialogFooter>
  );
};
